import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, applyActionCode } from 'firebase/auth';
import { Box, Typography, CircularProgress, Paper, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import app from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

const auth = getAuth(app);

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, fetchUserType } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      const oobCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');

      if (!oobCode) {
        setStatus('success');
        return;
      }

      if (mode !== 'verifyEmail') {
        setError('Invalid verification link');
        setStatus('error');
        setTimeout(() => navigate('/tenant-access/login'), 4000);
        return;
      }

      try {
        await applyActionCode(auth, oobCode);
        setStatus('success');
      } catch (err) {
        if (err.code === 'auth/invalid-action-code') {
          // Already verified — still show success
          setStatus('success');
          return;
        }

        let errorMessage = 'Failed to verify email.';
        if (err.code === 'auth/expired-action-code') {
          errorMessage = 'This verification link has expired. Please request a new one.';
        } else if (err.message) {
          errorMessage = `${err.message} (Code: ${err.code || 'unknown'})`;
        }

        setError(errorMessage);
        setStatus('error');
        setTimeout(() => navigate('/tenant-access/login'), 4000);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleContinue = async () => {
    try {
      if (currentUser) {
        const userType = await fetchUserType(currentUser.uid);
        if (userType === 'landlord' || userType === 'manager') {
          navigate('/agent-onboarding');
        } else {
          navigate('/onboarding');
        }
      } else {
        navigate('/tenant-access/login');
      }
    } catch (err) {
      console.error('Error fetching user type:', err);
      navigate('/tenant-access/login');
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="#f4f6f8"
    >
      <Paper
        elevation={3}
        sx={{ padding: '2rem', maxWidth: '500px', textAlign: 'center', borderRadius: '12px' }}
      >
        {status === 'verifying' && (
          <>
            <CircularProgress sx={{ marginBottom: '1rem' }} />
            <Typography variant="h5" gutterBottom>
              Verifying Your Email...
            </Typography>
            <Typography variant="body1">
              Please wait while we verify your email address.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: '4rem', marginBottom: '1rem' }}
            />
            <Typography variant="h5" gutterBottom>
              Email Verification Successful!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Dear {currentUser?.email || 'there'},<br />
              Welcome to Tenant Guided Tours! We're delighted to have you join our platform.
              Your email has been successfully verified, and you're now ready to explore
              a seamless way to connect tenants and landlords. Click below to begin your journey.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginTop: '1.5rem' }}
              onClick={handleContinue}
            >
              Get Started
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorOutlineIcon
              color="error"
              sx={{ fontSize: '4rem', marginBottom: '1rem' }}
            />
            <Typography variant="h5" gutterBottom>
              Verification Failed
            </Typography>
            <Typography variant="body1" gutterBottom>
              {error || 'An error occurred during verification.'}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: '1rem', color: '#666' }}>
              Redirecting to login page in 4 seconds...
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default VerifyEmail;